import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { FileText, Sheet, Loader2, FolderArchive, Image as ImageIcon, Share2 } from "lucide-react";
import { api } from "@/lib/api";
import { exportExcel, REPORT_SECTIONS } from "@/lib/exportUtils";
import { toast } from "sonner";
import { safeLS } from "@/lib/safeStorage";

export const ExportPorPuntos = ({ open, onOpenChange, data, exportOpts, mes, vendedor }) => {
  const [selected, setSelected] = useState(() => {
    try {
      const s = JSON.parse(safeLS.getItem("informe_secciones") || "null");
      if (Array.isArray(s) && s.length) return s;
    } catch (e) { /* noop */ }
    return REPORT_SECTIONS.map((s) => s.key);
  });
  const [detalle, setDetalle] = useState(() => safeLS.getItem("informe_detalle_judicial") === "1");
  const [wm, setWm] = useState(() => safeLS.getItem("informe_watermark") === "1");
  const [busy, setBusy] = useState(false);
  useEffect(() => { safeLS.setItem("informe_secciones", JSON.stringify(selected)); }, [selected]);
  useEffect(() => { safeLS.setItem("informe_detalle_judicial", detalle ? "1" : "0"); }, [detalle]);
  useEffect(() => { safeLS.setItem("informe_watermark", wm ? "1" : "0"); }, [wm]);
  const canShare = typeof navigator !== "undefined" && !!navigator.canShare;

  const toggle = (key) =>
    setSelected((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]));

  const allSelected = selected.length === REPORT_SECTIONS.length;
  const toggleAll = () =>
    setSelected(allSelected ? [] : REPORT_SECTIONS.map((s) => s.key));

  const run = async (kind) => {
    if (!selected.length) {
      toast.error("Selecciona al menos una sección para descargar.");
      return;
    }
    setBusy(true);
    try {
      if (kind === "pdf") {
        const r = await api.get("/informe-cartera.pdf", { params: { mes, vendedor, sections: selected.join(","), incluir_detalle_judicial: detalle, _t: Date.now() }, responseType: "blob" });
        const url = URL.createObjectURL(r.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Informe_Cartera_${(vendedor && vendedor !== "__all__") ? vendedor.replace(/[^A-Za-z0-9]+/g, "_") : "Todos"}_${mes}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      } else if (kind === "zip") {
        const r = await api.get("/informe-cartera.pdf", { params: { mes, vendedor: "__all__", sections: selected.join(","), incluir_detalle_judicial: detalle, por_vendedor_zip: true, _t: Date.now() }, responseType: "blob" });
        const url = URL.createObjectURL(r.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Informes_por_vendedor_${mes}.zip`;
        a.click();
        URL.revokeObjectURL(url);
      } else if (kind === "img" || kind === "share") {
        const r = await api.get("/informe-cartera.zip", { params: { mes, vendedor, sections: selected.join(","), watermark: wm }, responseType: "blob" });
        if (kind === "share" && navigator.canShare) {
          const file = new File([r.data], `Cartera_HD_${mes}.zip`, { type: "application/zip" });
          if (navigator.canShare({ files: [file] })) {
            try { await navigator.share({ files: [file], title: "Informe de Cartera", text: "Informe de Cartera (imágenes HD)" }); onOpenChange(false); setBusy(false); return; } catch { /* fallthrough to download */ }
          }
        }
        const url = URL.createObjectURL(r.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Cartera_HD_${(vendedor && vendedor !== "__all__") ? vendedor.replace(/[^A-Za-z0-9]+/g, "_") : "Todos"}_${mes}.zip`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Imágenes HD listas. Tip: en WhatsApp envialas como Documento para máxima calidad.", { duration: 6000 });
      } else {
        exportExcel(data, { sections: selected });
      }
      onOpenChange(false);
    } catch (e) {
      console.error("export por puntos error", e);
      toast.error("No se pudo generar la descarga. Vuelve a intentar.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="export-por-puntos-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-900">
            Descargar por puntos
          </DialogTitle>
          <DialogDescription>
            Elige qué secciones del informe quieres incluir en la descarga.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1">
          <label className="flex items-center gap-3 py-2 px-2 rounded-md hover:bg-emerald-50 cursor-pointer border-b border-slate-100 mb-1">
            <Checkbox
              data-testid="export-section-all"
              checked={allSelected}
              onCheckedChange={toggleAll}
            />
            <span className="text-sm font-semibold text-slate-800">Seleccionar todo</span>
          </label>
          {REPORT_SECTIONS.map((s) => (
            <label
              key={s.key}
              className="flex items-center gap-3 py-2 px-2 rounded-md hover:bg-emerald-50 cursor-pointer"
            >
              <Checkbox
                data-testid={`export-section-${s.key}`}
                checked={selected.includes(s.key)}
                onCheckedChange={() => toggle(s.key)}
              />
              <span className="text-sm text-slate-700">{s.label}</span>
            </label>
          ))}
        </div>

        <label className="flex items-center gap-3 py-2 px-2 mt-1 rounded-md bg-red-50/60 cursor-pointer" data-testid="export-detalle-judicial-row">
          <Checkbox data-testid="export-detalle-judicial" checked={detalle} onCheckedChange={() => setDetalle((v) => !v)} />
          <span className="text-sm text-slate-700">Incluir detalle de facturas en Casos judiciales <span className="text-slate-400">(alarga el PDF)</span></span>
        </label>
        <label className="flex items-center gap-3 py-2 px-2 mt-1 rounded-md bg-slate-50 cursor-pointer" data-testid="export-watermark-row">
          <Checkbox data-testid="export-watermark" checked={wm} onCheckedChange={() => setWm((v) => !v)} />
          <span className="text-sm text-slate-700">Marca de agua "Uso interno" en las imágenes HD</span>
        </label>
        <p className="px-2 pt-1 text-[11px] text-slate-400">El informe respeta el vendedor elegido arriba (Todos = agrupado por vendedor).</p>

        <div className="flex gap-2 pt-2">
          <Button
            data-testid="export-por-puntos-pdf"
            onClick={() => run("pdf")}
            disabled={busy}
            className="flex-1 bg-emerald-700 hover:bg-emerald-800"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
            Descargar PDF
          </Button>
          <Button
            data-testid="export-por-puntos-excel"
            onClick={() => run("excel")}
            disabled={busy}
            variant="outline"
            className="flex-1 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
          >
            <Sheet className="h-4 w-4 mr-2" /> Descargar Excel
          </Button>
        </div>
        <div className="flex gap-2 mt-2">
          <Button
            data-testid="export-por-puntos-img"
            onClick={() => run("img")}
            disabled={busy}
            variant="outline"
            className="flex-1 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ImageIcon className="h-4 w-4 mr-2" />}
            Descargar imagen HD
          </Button>
          {canShare && (
            <Button
              data-testid="export-por-puntos-share"
              onClick={() => run("share")}
              disabled={busy}
              className="flex-1 bg-[#25D366] hover:bg-[#1eb457] text-white"
            >
              <Share2 className="h-4 w-4 mr-2" /> Compartir
            </Button>
          )}
        </div>
        <p className="px-2 pt-1.5 text-[11px] text-slate-400">💡 Para máxima calidad en WhatsApp, enviá la imagen como <strong>Documento</strong>.</p>
        {(!vendedor || vendedor === "__all__") && (
          <Button
            data-testid="export-por-puntos-zip"
            onClick={() => run("zip")}
            disabled={busy}
            variant="outline"
            className="w-full mt-2 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FolderArchive className="h-4 w-4 mr-2" />}
            Un PDF por vendedor (.zip)
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ExportPorPuntos;
